<script setup lang="ts">
import { X, MessageCircle } from 'lucide-vue-next';
import ChatWindow from './ChatWindow.vue';
import type { Globals } from '#shared/types/schema';

interface Props {
	globals?: Globals | null;
}

const props = defineProps<Props>();
const runtimeConfig = useRuntimeConfig();

const isOpen = ref(false);

const toggleChat = () => {
	isOpen.value = !isOpen.value;
};

// Get chatbot avatar URL
const chatbotAvatarUrl = computed(() => {
	if (!props.globals?.chatbot_avatar) return null;
	
	const avatarId = typeof props.globals.chatbot_avatar === 'string' 
		? props.globals.chatbot_avatar 
		: props.globals.chatbot_avatar.id;
	
	return `${runtimeConfig.public.directusUrl}/assets/${avatarId}`;
});
</script>

<template>
	<div class="fixed bottom-6 right-6 z-50">
		<!-- Chat Window -->
		<Transition
			enter-active-class="transition-all duration-300 ease-out"
			enter-from-class="opacity-0 translate-y-4 scale-95"
			enter-to-class="opacity-100 translate-y-0 scale-100"
			leave-active-class="transition-all duration-200 ease-in"
			leave-from-class="opacity-100 translate-y-0 scale-100"
			leave-to-class="opacity-0 translate-y-4 scale-95"
		>
			<div
				v-if="isOpen"
				class="fixed top-4 left-4 right-4 bottom-24 md:inset-auto md:absolute md:bottom-24 md:right-0 w-auto md:w-[400px] h-auto md:h-[600px] md:max-h-[calc(100vh-10rem)] bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-800 flex flex-col overflow-hidden"
			>
				<ChatWindow :avatarUrl="chatbotAvatarUrl" @close="isOpen = false" />
			</div>
		</Transition>

		<!-- Chat Button -->
		<button
			@click="toggleChat"
			class="w-14 h-14 rounded-full bg-black text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 flex items-center justify-center overflow-hidden"
			aria-label="Apri chat"
		>
			<Transition
				mode="out-in"
				enter-active-class="transition-all duration-200"
				enter-from-class="opacity-0 rotate-90"
				enter-to-class="opacity-100 rotate-0"
				leave-active-class="transition-all duration-200"
				leave-from-class="opacity-100 rotate-0"
				leave-to-class="opacity-0 -rotate-90"
			>
				<template v-if="!isOpen">
					<img
						v-if="chatbotAvatarUrl"
						:src="chatbotAvatarUrl"
						alt="Chatbot Avatar"
						class="w-8 h-8 object-contain"
						key="open-avatar"
					/>
					<MessageCircle v-else :size="24" key="open-icon" />
				</template>
				<X v-else :size="24" key="close" />
			</Transition>
		</button>
	</div>
</template>

